import FormErrors from 'app/utils/form_errors'
import deserialize from 'app/utils/deserialize'
import { fetch, create, update, remove } from 'app/utils/data'
import { get, size } from 'lodash'

/**
 * Defines the specific data and functions necessary for account creation, modification, and deletion.
 */
export default {
  namespaced: true,

  state: {
    list: [],
    item: null
  },

  getters: {
    currentAccount: (state) => state.item,

    isAdmin: (state) => state.item && state.item.admin,

    isAffiliate: (state) => size(get(state, 'item.affiliate')) !== 0,

    affiliate: (state) => {
      const affiliate = get(state, 'item.affiliate', null)
      return size(affiliate) === 0 ? null : affiliate
    },

    seatAssignmentLicense: (state) => state.item.seatAssignmentLicense,

    hasAssignableSeats: (_state, getters) => !!getters.seatAssignmentLicense,

    totalAssignableSeats: (_state, getters) => getters.seatAssignmentLicense && getters.seatAssignmentLicense.seats
  },

  mutations: {
    accountFetched(state, { account }) {
      state.item = account
    },

    subscriptionFetched(state, { subscription }) {
      state.item.subscription = subscription
    },

    resetState(state) {
      state.list = []
      state.item = null
    }
  },

  actions: {
    sendInvite({ commit }, { email }) {
      return new Promise((resolve, reject) => {
        const data = {
          email
        }

        create({
          url: '/api/account-invitation',
          type: 'account-invitation',
          data
        })
          .then((response) => {
            resolve()
          })
          .catch((error) => {
            reject(error)
          })
      })
    },

    changePassword({ state }, changes) {
      return new Promise((resolve, reject) => {
        update({
          url: `/api/account/password`,
          type: 'account',
          changes,
          record: state.item
        })
          .then((response) => {
            resolve()
          })
          .catch(({ response }) => {
            reject(new FormErrors(response.data.errors))
          })
      })
    },

    beginChangeEmail({ state }, email) {
      return new Promise((resolve, reject) => {
        update({
          url: `/api/account`,
          type: 'account',
          changes: { email },
          record: state.item
        })
          .then(() => {
            // This probably means that no changes will take place (ie: the email is the same)
            reject()
          })
          .catch(({ response }) => {
            if (response.status === 409) {
              resolve()
            } else {
              reject(new FormErrors(response.data.errors))
            }
          })
      })
    },

    finishChangeEmail({ commit, state }, { email, emailChangeToken, password }) {
      return new Promise((resolve, reject) => {
        update({
          url: `/api/account`,
          type: 'account',
          changes: { email },
          meta: {
            emailChangeToken,
            password
          },
          record: state.item
        })
          .then((response) => {
            deserialize(response.data).then((account) => {
              commit('accountFetched', { account })
              resolve()
            })
          })
          .catch((error) => {
            reject(new FormErrors(error.response.data.errors))
          })
      })
    },

    /**
     * Takes the user provided email and posts it to the account creation API. The API response is deserilized and commited
     * to the apps global state via the Vuex commit action handler.
     *
     * Details:
     * Vuex commit function is passed in by caller and made available via destructuring assignment.
     * Email is passed in by caller.
     * Function calls {@link ../../utils/data.js#create} and awaits response.
     * Response is deserialized and passed to the provided commit function.
     * Response example:
     * {"hasPassword":false,
     * "email":"test@test.com",
     * "confirmationToken":"ca2276bbb9af49f1916e2ab06f022968",
     * "id":"8b2fe584bfbf4886831368337dbbe665"}
     *
     * Convenience Links:
     * {@link https://v3.vuex.vuejs.org/guide/actions.html Vuex Actions}
     *
     * @async
     * @function createFreeAccount
     * @param {Object} param0 - The Vuex commit and dispatch objects
     * @param {import('vuex').Commit} param0.commit - The Vuex commit object
     * @param {string} email - The users email address
     * @returns {Promise|undefined} - The response to the creation request or nothing
     */
    async createFreeAccount({ commit }, email) {
      try {
        const response = await create({
          url: '/api/account',
          type: 'account',
          data: { email }
        })

        const account = await deserialize(response.data)
        commit('accountFetched', { account })
      } catch (error) {
        throw new FormErrors(error.response.data.errors)
      }
    },

    async updateAccount({ commit, state, dispatch }, changes) {
      try {
        const response = await update({
          url: '/api/account',
          patch: true,
          type: 'account',
          changes,
          record: state.item
        })

        const account = await deserialize(response.data)

        commit('accountFetched', { account })

        dispatch('sessions/fetchCurrentSession', { force: true }, { root: true })
      } catch (error) {
        throw new FormErrors(error.response.data.errors)
      }
    },

    async deleteAccount({ commit, state, dispatch }, { password }) {
      try {
        await remove({
          url: '/api/account',
          meta: { password }
        })

        dispatch('sessions/resetEverything', {}, { root: true })
      } catch (error) {
        throw new FormErrors(error.response.data.errors)
      }
    },

    async getAccountData() {
      try {
        const response = await fetch({
          url: `/api/account/personal-data`,
          type: 'personal-data'
        })
        return await response.data
      } catch (error) {
        throw new FormErrors(error.response.data.errors)
      }
    },

    async changePaymentMethod({ commit, state, dispatch }, accountId) {
      const response = await update({ url: `/api/account/${accountId}/payment-method` })

      const account = await deserialize(response.data)

      commit('accountFetched', { account })
    }
  }
}
